/**
 * Rules for turning a stored competency list into what a teacher sees for one
 * term. Shared by the entry grid and the printed form.
 *
 * The PACE form carries two structurally different shapes under one cover:
 *
 *   A  Reading and Literacy, Language. Competencies are shared across all three
 *      terms, and each term offers one rating per language macro skill -
 *      Listening, Speaking, Reading, Copying and Guided Writing - shown on the
 *      printed form by cell colour.
 *
 *   B  Mathematics, GMRC, Makabansa. Each competency belongs to one term and
 *      takes a single rating.
 */

export const MACRO_SKILL_ORDER = ['L', 'S', 'R', 'W'];

export const MACRO_SKILL_LABELS = {
  L: 'Listening',
  S: 'Speaking',
  R: 'Reading',
  W: 'Copying and Guided Writing',
};

export function isSharedAcrossTerms(competency) {
  return Boolean(competency) && competency.shape === 'A';
}

/**
 * Which competencies appear for a term. Shared ones always do; term-scoped ones
 * only in their own term.
 */
export function competenciesForTerm(competencies, term) {
  const index = parseInt(term, 10);
  return (competencies || []).filter((competency) =>
    isSharedAcrossTerms(competency) ? true : competency.term === index
  );
}

/**
 * Which macro skills are rateable for a competency in a term.
 *
 * Returns [''] for a term-scoped competency, meaning one unnamed rating. An
 * empty array means the row is greyed for that term on the printed form and
 * takes no rating at all. Header rows are group stems and never rateable.
 *
 * Stored keys may be numbers or strings depending on how the record came back
 * from Mongo, so both are checked.
 */
export function skillsForTerm(competency, term) {
  if (!competency || competency.isHeader) return [];
  if (!isSharedAcrossTerms(competency)) return [''];

  const perTerm = competency.macroSkills || {};
  const skills = perTerm[term] || perTerm[String(term)] || [];

  // Keep the form's print order rather than whatever order they were stored in.
  return MACRO_SKILL_ORDER.filter((skill) => skills.indexOf(skill) !== -1);
}

/**
 * Which macro skills a subject actually uses, derived from its competencies.
 *
 * The printed form carries a different legend per subject: Reading and Literacy
 * shows all four skills, Language shows only Listening and Speaking. Rendering
 * the full set everywhere makes Language look like two selectors are missing
 * when in fact it only ever had two.
 */
export function skillsUsedBy(competencies) {
  const used = {};
  (competencies || []).forEach((competency) => {
    if (!isSharedAcrossTerms(competency)) return;
    const perTerm = competency.macroSkills || {};
    Object.keys(perTerm).forEach((term) => {
      (perTerm[term] || []).forEach((skill) => { used[skill] = true; });
    });
  });
  return MACRO_SKILL_ORDER.filter((skill) => used[skill]);
}

/** Key under which a rating is held in the entry grid's local state. */
export function ratingKey(competencyId, macroSkill) {
  return `${competencyId}|${macroSkill || ''}`;
}

/**
 * Turns the grid's local state into a bulk-save payload, dropping blanks. A
 * blank is a competency the teacher has not reached yet, not a rating of
 * nothing, so sending it would create empty rows.
 */
export function toBulkPayload(ratings) {
  return Object.keys(ratings || {})
    .filter((key) => Boolean(ratings[key]))
    .map((key) => {
      const separator = key.lastIndexOf('|');
      return {
        competencyId: key.slice(0, separator),
        macroSkill: key.slice(separator + 1),
        rating: ratings[key],
      };
    });
}

/**
 * How many ratings a term still expects, so the UI can show progress. Counts
 * every rateable slot, not every row: one Reading and Literacy competency can
 * carry up to four.
 */
export function countSlots(competencies, term) {
  return competenciesForTerm(competencies, term).reduce(
    (total, competency) => total + skillsForTerm(competency, term).length,
    0
  );
}

export function countFilled(competencies, term, ratings) {
  return competenciesForTerm(competencies, term).reduce((total, competency) => {
    const filled = skillsForTerm(competency, term).filter(
      (skill) => Boolean((ratings || {})[ratingKey(competency.competencyId, skill)])
    ).length;
    return total + filled;
  }, 0);
}
